/**
 * /force-password-change — interstitial that the user is held on until they
 * change a temporary password (V3-FIX-NEW-5; research-B §8.4).
 *
 * Phase-3 W4.T6 extracted the form internals into the shared
 * `<PasswordChangeForm>` so the same chrome powers the /profile self-service
 * tab (D01). This page now owns ONLY the routing guards + page-shell layout;
 * field rendering, validation, and submit semantics live in the shared form.
 *
 * Why a dedicated route (vs. reusing the change-password section in
 * /profile)?
 *   1. Single-purpose URL: clearer history breadcrumb, easier to e2e.
 *   2. Hard guard: the page short-circuits to "/" when the flag is false so
 *      the URL is never reachable for normal users (e.g. a stale tab where
 *      the password was already changed elsewhere).
 *   3. Mirrors the BE: the API uses `mustChangePassword` as the only
 *      blocking condition; this page is the FE manifestation.
 *
 * Guard layering:
 *   - <RequireAuth> still runs first (token + me query).
 *   - When `mustChangePassword === true` it routes the user HERE, never to
 *     a freely-navigable target like /profile.
 *   - When the user lands here with the flag CLEARED, this page itself
 *     redirects to "/" via <Navigate replace />. That second guard avoids a
 *     "logged-in user permanently stuck on /force-password-change" footgun
 *     if their flag clears mid-session.
 *
 * Submit flow lives inside `<PasswordChangeForm>`. After success the form
 * refetches `qk.auth.session()` so the `mustChangePassword` flag is
 * guaranteed cleared before this page reroutes to "/" via `onSuccess`.
 */
import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { PasswordChangeForm } from '@/components/auth/password-change-form';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';

export function ForcePasswordChangePage(): React.ReactElement {
  const navigate = useNavigate();
  const { data: me, isLoading } = useMe();

  // Loading: a tiny skeleton card matching the form silhouette so we don't
  // flash the form for the cleared-flag user.
  if (isLoading) {
    return (
      <div data-testid="force-pwd-loading" className="mx-auto max-w-md space-y-3 p-6">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  // Hard guard: someone arriving here without the flag (e.g. the BE just
  // cleared it on another tab, or someone deep-linked) gets bounced.
  if (!me || !me.mustChangePassword) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      data-testid="force-pwd-page"
      className="mx-auto flex max-w-md flex-col gap-6 rounded-md border border-line bg-surface p-6 shadow-1"
    >
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-ink">Смените пароль</h1>
        <p className="text-sm text-muted-foreground">
          Дальнейшее использование требует смены пароля.
        </p>
      </header>
      <PasswordChangeForm
        mode="force"
        // The temp password (sent in the invite email) IS the user's current
        // password — we collect + verify it before granting the rotation.
        // The shared form prop lets a future first-login flow flip this to
        // false without touching this page again.
        requireOldPassword={true}
        autoFocusFirstField
        onSuccess={() => {
          navigate('/', { replace: true });
        }}
      />
    </div>
  );
}
